import { createContext, ReactNode, useContext, useState } from 'react'
import { Col, Container, FlexboxGrid, Nav, Row } from 'rsuite'
import AddIcon from '@rsuite/icons/Plus'
import CloseIcon from '@rsuite/icons/Close'
import Button from './Button'
import Table from './Table'
import useQuery from '../../utils/useQuery'
import { ITableListModel } from '../../utils/db'
import ModalDialog from '../ModalDialog'
import Loading from '../Loading'
import { merge } from '@/shared'
import { SortType } from 'rsuite/esm/Table'
import { useTranslation } from 'react-i18next'

// --- types ---

export type TTabItem = { mode: string, id: string, data?: any, label: string, autoScroll?: boolean }
export type TTab = { key: string, label: string, itemProps: TTabItem, formData?: TTabItem, modified: boolean, error: boolean, autoScroll: boolean }
export type TTabListState = { tabListId: string, tabs: TTab[], activeTab: string }
export type TTabState = { tabs: TTab[], activeTab: string }

export type TTabList = {
    tabListId: string,
    tabTitle?: string,
    emptyText?: string,
    tabAddHint?: string,
    tabAddText?: string,
    model: ITableListModel
    handleTabList?: (action: string, data: any) => any
    children: ReactNode
}

export type TTabListEdit = { tab: TTab }
export type TTabListFormData = any

type TTabHeader = { tabListId: string, tabTitle?: string, tabAddHint?: string, tabs: TTab[], activeTab: string }
type TTabListContent = { model: ITableListModel, activeTab: string, emptyText?: string, children: ReactNode }

// --- styles ---

export const TabListFooterStyle = {
    display: 'flex',
    flexFlow: 'column wrap',
    alignItems: 'start',
    justifyContent: 'start',
    marginTop: '2em',
} as React.CSSProperties
export const TabListFooterErrorWrapperStyle = {
    display: 'flex',
    marginBottom: '2em',
    width: '100%',
} as React.CSSProperties
export const TabListFooterErrorStyle = {
    borderRadius: 6,
    border: '1px solid hsl(0deg 100% 40%)',
    backgroundColor: 'hsl(0deg 100% 97%)',
    color: 'hsl(0deg 100% 40%)',
    padding: '5px 10px',
    display: 'flex',
    width: '100%',
} as React.CSSProperties

const tabListStyle = {
}
const tabHeaderStyle = {
}
const tabContentStyle = {
    // backgroundImage: 'linear-gradient(-22deg, var(--color-2), var(--color-3))',
    border: '1px solid #fff',
    borderTop: 'none',
    flex: 1,
}

// --- tablist context ---

export type TabListContext = {
    cmd: (action: string, data?: any, defaultValue?: any) => any,
    findTab: (activeTab: string) => any,
    handleSaveTab: (id: string, data: Object) => any,
    handleTabChanged: (activeTab: string) => void,
    handleHeaderChanged: (tab: TTab, data: any) => any,
    handleOpen: (mode: string, id: string, data?: any) => void,
    handleClose: (e: Event, eventKey: string) => void,
    handleCellAction: (action: string, id?: string, data?: any) => any,
}
const Context = createContext<TabListContext>({
    cmd(action, data, defaultValue) { },
    findTab(activeTab) { },
    handleSaveTab(id, data) { },
    handleTabChanged(activeTab) { },
    handleHeaderChanged(tab, data) { },
    handleOpen(mode, id, data) { },
    handleClose(e, eventKey) { },
    handleCellAction(action) { },
})

export const useTabList = () => {
    return useContext(Context)
}

// --- store status of all TabLists ---

type TTabListContainer = { list: TTabListState[] }
const tabListContainer: TTabListContainer = {
    list: []
}

const getTabList = (tabListId: string, activeTab?: string) => {
    if (!tabListId) return null
    const tl = tabListContainer.list.find((item: TTabListState) => item.tabListId === tabListId)
    if (!tl) return { tabListId: tabListId, tabs: [], activeTab } as TTabListState

    if (activeTab) tl.activeTab = activeTab

    return tl
}

const updateTabList = (tabListId: string, tabList: TTabListState | null, tabs: TTab[] | null) => {
    if (!tabList || !tabs) return false
    const newTabList = { ...tabList, ...{ tabs: tabs } }
    const filteredTabList = tabListContainer.list.filter(item => item.tabListId !== tabListId)
    filteredTabList.push(newTabList)
    tabListContainer.list = filteredTabList
    return true
}

// --- tablist header ---

const TabListHeader = ({
    tabListId,
    tabTitle,
    tabAddHint,
    tabs,
    activeTab,
}: TTabHeader) => {
    const tabList = useContext(Context)
    const tabHasError = (error: any) => {
        if (!error) return false
        return !!Object.keys(error)
    }

    function NavHeaderItems() {
        return (
            <>
                {
                    tabs.map((item: TTab) => {
                        return <Nav.Item
                            key={item.key}
                            eventKey={item.key}
                            className={"tablist-nav-item" + (tabHasError(item.error) ? ' error' : '') + (item.modified ? ' modified' : '')}
                        >
                            {item.label}
                            <span
                                className="tablist-header-button"
                                onClick={(e: any) => tabList.handleClose(e, item.key)}>
                                <CloseIcon />
                            </span>
                        </Nav.Item>
                    })
                }
            </>)
    }

    function handleSelect(eventKey: string) {
        if (eventKey == 'add-tab') tabList.handleOpen('add', tabListId, '')
        else tabList.handleTabChanged(eventKey)
    }

    return (
        <Nav
            appearance="tabs"
            activeKey={activeTab}
            onSelect={handleSelect}
            className="tablist-nav"
            style={tabHeaderStyle}
        >
            <Nav.Item key={tabListId} eventKey={tabListId}>{tabTitle}</Nav.Item>
            <NavHeaderItems />
            <Nav.Item key="add-tab" title={tabAddHint} eventKey="add-tab" ><AddIcon /></Nav.Item>
        </Nav>
    )
}

// --- tablist content ---

const TabListContent = ({ model, activeTab, emptyText, children }: TTabListContent) => {
    const { t } = useTranslation()
    const tabList = useContext(Context)
    const [page, setPage] = useState<number>(1)
    const [sortColumn, setSortColumn] = useState<string>();
    const [sortType, setSortType] = useState<SortType>();
    const { loading, error, data } = useQuery(model.list, { args: { page: page, count: 10, sortField: sortColumn, sortOrder: sortType } });
    const canSort = data && data.length > 0

    if (error) return `Error! ${error.message}`
    if (loading) return <Loading />

    const tab = tabList.findTab(activeTab)
    const autoScroll = typeof tab?.autoScroll === 'boolean' ? tab.autoScroll : true

    const prevPage = () => {
        setPage((page) => page - 1 < 1 ? 1 : page - 1)
    }
    const nextPage = () => {
        setPage((page) => page + 1 > data.pageCount ? data.pageCount : page + 1)
    }
    const firstPage = () => {
        setPage((page) => 1)
    }
    const lastPage = () => {
        setPage((page) => data.pageCount)
    }

    const handleSort = (column: string, sortOrder?: SortType) => {
        setSortColumn(column);
        setSortType(sortOrder);
    }

    const renderEmpty = () => {
        const text = emptyText || t('No data found.')
        return (
            <div className="rs-table-body-info">{text}</div>
        )
    }

    const renderContent = () => {
        if (tab) {
            console.log(`TabListContent:render ${activeTab}`)
            return (
                <Container className={`tab-content tab-content-${activeTab}`} >
                    {tabList.cmd('getTabContent', { activeTab, tab })}
                </Container >
            )
        }

        // return list of data
        return (
            <>
                <Table
                    className="tablist-table"
                    loading={loading}
                    wordWrap="break-word"
                    fillHeight={true}
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onSortColumn={handleSort}
                    handleCellAction={tabList.handleCellAction}
                    showHeader={true}
                    data={data.rows}
                    renderEmpty={renderEmpty}
                >
                    {children}

                    {
                        data.pageCount > 1 && <Row className="tablist-table-footer" justify='start'>
                            <Col>
                                <Button onClick={firstPage} appearance="primary">&lt;&lt;</Button>
                                <Button onClick={prevPage} appearance="primary">&lt;</Button>
                                <Button onClick={nextPage} appearance="primary">&gt;</Button>
                                <Button onClick={lastPage} appearance="primary">&gt;&gt;</Button>
                            </Col>
                        </Row>
                    }

                </Table >

                {
                    data.pageCount > 1 && <FlexboxGrid className="tablist-table-footer" justify='start'>
                        <Button onClick={firstPage} appearance="primary">&lt;&lt;</Button>
                        <Button onClick={prevPage} appearance="primary">&lt;</Button>
                        <Button onClick={nextPage} appearance="primary">&gt;</Button>
                        <Button onClick={lastPage} appearance="primary">&gt;&gt;</Button>
                    </FlexboxGrid>
                }
            </>
        )
    }

    return (
        <Container className={"tablist-content" + (autoScroll ? ' autoscroll' : '')} style={tabContentStyle}>
            {renderContent()}
        </Container>
    )
}

// --- tablist ---

export const TabList = ({
    tabListId,              // unique tablist id 
    tabTitle,               // tablist title
    emptyText,              // Text if table has no data
    tabAddHint,             // tablist hint text
    tabAddText,             // tablist new tab title
    handleTabList,          // handle all calls inside the tablist
    model,                  // the data model
    children                // TabList child elements
}: TTabList) => {
    console.log(`Start TabList ${tabListId} ...`)
    const { t } = useTranslation()
    const [refresh, setRefresh] = useState<number>(0)
    const [modifiedDialog, setModifiedDialog] = useState<TTab | null>(null)
    const [deleteDialog, setDeleteDialog] = useState<string | null>(null)
    const [activeTab, setActiveTab] = useState<any>(() => {
        const tl = getTabList(tabListId)
        return tl && tl.activeTab ? tl.activeTab : tabListId
    })

    // --- store ---

    const tabList = getTabList(tabListId, activeTab)
    const tabs = tabList?.tabs || []

    const updateTab = (activeKey: string, tab: TTab) => {
        if (!activeKey || !tab) return

        let found = false
        const newTabs = (tabs
            ? tabs.map(item => {
                if (item.key !== tab.key) return item
                found = true
                return tab
            })
            : [])

        if (newTabs && !found) newTabs.push(tab)
        updateTabList(tabListId, tabList, newTabs)
    }

    const deleteTab = (tabListId: string, key?: string) => {
        if (!tabListId || !key) return false
        const tabs = tabList?.tabs?.filter(item => item.key !== key) || []
        updateTabList(tabListId, tabList, tabs)
        return true
    }

    // --- store end ---

    /**
     * handling all custom actions of the tab list with this function
     * @param action 
     * @param data 
     * @param defaultValue 
     * @returns 
     */
    const cmd = (action: string, data?: any, defaultValue: any = '') => {
        if (!handleTabList) return defaultValue
        return handleTabList(action, data)
    }

    const refreshList = () => {
        setRefresh(refresh + 1)
    }

    const handleTabChanged = (tabKey: string) => {
        setActiveTab(tabKey)
    }

    const handleHeaderChanged = (tab: TTab, data: any) => {
        console.log('handleHeaderChanged:', tab, data)
        updateTab(tabListId, tab)
        refreshList()
    }

    const handleOpen = (mode: string, id: string, data?: any) => {
        if (mode === 'delete') {
            setDeleteDialog(id)
        }
        else if (mode === 'add') {
            handleTabChanged(addTab({
                mode: 'add',
                id: '',
                data: cmd('getDefault'),
                label: cmd('getLabel', null),
                autoScroll: cmd('autoScroll', true),
            }))
        }
        else /* edit */ {
            handleTabChanged(addTab({
                mode,
                id,
                data,
                label: cmd('getLabel', data),
                autoScroll: cmd('autoScroll', true),
            }))
        }
    }

    const handleClose = (e: Event, eventKey: string) => {
        e.preventDefault()
        e.stopPropagation()

        const tab = findTab(eventKey)
        if (!tab) return

        if (tab.modified) {
            setModifiedDialog(tab)
        }
        else {
            removeTab(eventKey)
        }
    }

    const handleModified = () => {
        if (!modifiedDialog) return

        const buttons = [
            { status: 'save', label: t('Save') },
            { status: 'reject', label: t('Reject') },
            { status: 'cancel', label: t('Cancel') },
        ]
        return <ModalDialog
            title={t('Data has changed')}
            content={t('The data has changed.<br/>What do you want to do with the data?')}
            buttons={buttons}
            onClick={handleCloseModifiedDialog}
        />
    }

    const handleCloseModifiedDialog = async (status: string) => {
        if (status === 'cancel') {
            setModifiedDialog(null)
            return true
        }

        // ignore status == 'reject'
        if (status === 'save') {
            const tab = findTab(activeTab)
            if (! await handleSaveTab(tab.itemProps.id, tab.formData as Object)) return
        }

        removeTab(modifiedDialog?.key)
        setModifiedDialog(null)
        return true
    }

    const handleSaveTab = async (id: string, data: Object) => {
        const savedData = await cmd('saveTab', { id, data })
        if (savedData) {
            const tab = findTab(activeTab)
            if (!tab) return false

            const newData = merge(tab.formData, data)
            const label = cmd('getLabel', newData)
            const itemProps = { data: savedData, id: savedData._id, label: label, mode: 'edit' }
            const newTab = merge(tab, { modified: false, itemProps: itemProps, label: label })
            handleHeaderChanged(newTab, data)
        }
        return savedData;
    }

    const handleDeleteRow = () => {
        if (!deleteDialog) return

        const buttons = [
            { status: 'delete', label: t('Delete') },
            { status: 'cancel', label: t('Cancel') },
        ]
        return <ModalDialog
            title={t('Delete list item')}
            content={t('Do you really want to delete the dataset?')}
            buttons={buttons} onClick={handleDeleteDialog}
            params={{ id: deleteDialog }}
        />
    }

    const handleDeleteDialog = async (status: string, params: { id: string }) => {
        if (status === 'cancel') {
            setDeleteDialog(null)
            return true
        }

        // ignore status == 'reject'
        if (status === 'delete') {
            const done = await cmd('deleteTab', params.id)
            if (!done) return false
        }

        removeTab(modifiedDialog?.key)
        return true
    }

    const handleClick = (mode: string, id: string, data: any) => {
        if (mode === 'add') {
            handleTabChanged(addTab({
                mode: 'add',
                id: '',
                data: cmd('getDefault'),
                label: cmd('getLabel', null),
                autoScroll: cmd('autoScroll', true),
            }))
        }
        else if (mode === 'edit') {
            handleTabChanged(addTab({
                mode,
                id,
                data,
                label: cmd('getLabel', data),
                autoScroll: cmd('autoScroll', true),
            }))
        }
        else if (mode === 'delete') {
            setDeleteDialog(id || '')
        }

        return null
    }

    const handleCellAction = (action: string) => {
        const params = {
            label: '',
            className: '',
            onClick: handleClick
        }

        const response = cmd('getCellAction', { name: action, data: params })
        if (response) return response
        return params
    }

    const addTab = (params: TTabItem) => {
        const existingTab = params.mode == 'edit' ? findTabById(params.id) : null
        if (existingTab) return existingTab.key

        const d = new Date()
        const key = '' + d.getSeconds() + d.getMilliseconds()

        let tabData;
        if (params.mode === 'edit') {
            tabData = params.data
        }
        else if (params.mode === 'add' && tabAddText) {
            tabData = { name: tabAddText }
        }

        const label = params?.label ? params.label : (tabData ? tabData.name : t('New item'))
        const autoScroll = typeof params?.autoScroll === 'boolean' ? params.autoScroll : true

        const newTab: TTab = {
            key: key,
            label: label,
            itemProps: params,
            formData: params.data,
            modified: false,
            error: false,
            autoScroll: autoScroll,
        }
        updateTab(key, newTab)

        return key
    }

    const findTab = (key: string) => {
        return tabs.find((tab) => tab.key === key) as TTab
    }

    const findTabById = (id: string) => {
        return tabs.find((tab) => tab.itemProps.id === id) as TTab
    }

    const removeTab = (key?: string) => {
        const index = tabs.findIndex((tab) => tab.key === key) - 1
        const nextKey = index >= 0
            ? tabs[index].key
            : tabListId

        deleteTab(tabListId, key)

        if (activeTab === key) {
            handleTabChanged(nextKey)
        } else {
            refreshList()
        }
    }

    const value: TabListContext = {
        cmd,
        findTab,
        handleTabChanged,
        handleHeaderChanged,
        handleSaveTab,
        handleOpen,
        handleClose,
        handleCellAction,
    }

    return (
        <Context.Provider value={value} >
            <Container key={tabListId} className="tablist-container content-view content-items" style={tabListStyle}>
                <TabListHeader
                    tabListId={tabListId}
                    activeTab={activeTab}
                    tabAddHint={tabAddHint}
                    tabTitle={tabTitle}
                    tabs={tabs}
                />

                <TabListContent model={model} activeTab={activeTab} emptyText={emptyText}>
                    {children}
                </TabListContent>

                {modifiedDialog && handleModified()}
                {deleteDialog && handleDeleteRow()}
            </Container>
        </Context.Provider>
    )
}

//  --- sub elementes ---

TabList.Column = Table.Column
TabList.HeaderCell = Table.HeaderCell
TabList.Row = Table.Row
TabList.Cell = Table.Cell
TabList.CellAction = Table.CellAction